import type {
  ApplicationStudioArtifactKind,
  FeatureKey,
  PaidPlanTier,
} from '@tabbio/types';
import { PHONE_CODES } from './country-data';
import { localizedCountryName, normalizeSearchText } from './country-names';

export type ApplicationStudioToolId =
  | 'cvScore'
  | 'coverLetter'
  | 'interviewPrep'
  | 'salaryInsights'
  | 'applicationPack'
  | 'recruiterPreview';

/**
 * The product event each studio tool fires on a successful generation. Specific
 * names for PostHog; GA4 receives them rolled up as `feature_used` through the
 * adapter's `GA4_FEATURE_ROLLUP` table, which must list every name here.
 */
export const STUDIO_TOOL_ANALYTICS_EVENTS: Readonly<
  Record<ApplicationStudioToolId, string>
> = {
  cvScore: 'cv_score_checked',
  coverLetter: 'cover_letter_created',
  interviewPrep: 'interview_prep_started',
  salaryInsights: 'salary_insight_viewed',
  applicationPack: 'application_pack_created',
  recruiterPreview: 'recruiter_preview_generated',
};

export type StudioCountryCode = string;
export type StudioSeniority = 'junior' | 'mid' | 'senior' | 'lead';

export interface ApplicationStudioToolConfig {
  id: ApplicationStudioToolId;
  route: string;
  featureKey: FeatureKey;
  recommendedTier: PaidPlanTier;
  generateKind: ApplicationStudioArtifactKind;
  resultKinds: ApplicationStudioArtifactKind[];
  titleKey: string;
  subtitleKey: string;
  outcomeKeys: string[];
  generateKey: string;
  rolePlaceholderKey: string;
  companyVisible: boolean;
  countryVisible: boolean;
  seniorityVisible: boolean;
  jobDescriptionVisible: boolean;
  notesVisible: boolean;
}

const GCC_STUDIO_COUNTRY_OVERRIDES: Record<
  string,
  { value: StudioCountryCode; label: string }
> = {
  AE: { value: 'UAE', label: 'UAE' },
  SA: { value: 'KSA', label: 'KSA' },
  QA: { value: 'QAT', label: 'Qatar' },
  KW: { value: 'KWT', label: 'Kuwait' },
  OM: { value: 'OMN', label: 'Oman' },
  BH: { value: 'BHR', label: 'Bahrain' },
};

export const STUDIO_COUNTRIES: {
  value: StudioCountryCode;
  label: string;
  isoCode: string;
}[] = PHONE_CODES.map((country) => {
  const override = GCC_STUDIO_COUNTRY_OVERRIDES[country.code];
  return {
    value: override?.value ?? country.code,
    label: override?.label ?? country.code,
    isoCode: country.code,
  };
});

// Precomputed, normalized search terms per country so the pickers can match a
// query against the ISO code, English label/name AND the Arabic dataset name.
// Built once at module load to keep per-keystroke filtering cheap.
const STUDIO_COUNTRY_SEARCH: {
  option: (typeof STUDIO_COUNTRIES)[number];
  terms: string;
}[] = STUDIO_COUNTRIES.map((option) => {
  const meta = PHONE_CODES.find((country) => country.code === option.isoCode);
  const terms = [
    option.value,
    option.label,
    option.isoCode,
    meta?.name ?? '',
    localizedCountryName(option.isoCode, 'en', meta?.name),
    localizedCountryName(option.isoCode, 'ar', ''),
  ]
    .map(normalizeSearchText)
    .join('\n');
  return { option, terms };
});

/**
 * Locale-tolerant country search shared by the native and web
 * StudioCountryPicker. The query is normalized and matched as a substring
 * against every localized name/code, so an Arabic query resolves against the
 * Arabic dataset name (issue #69): previously only Latin strings were tested.
 */
export function filterStudioCountries(query: string) {
  const q = normalizeSearchText(query);
  if (!q) return STUDIO_COUNTRIES;
  return STUDIO_COUNTRY_SEARCH.filter((entry) => entry.terms.includes(q)).map(
    (entry) => entry.option,
  );
}

export const STUDIO_SENIORITIES: { value: StudioSeniority; key: string }[] = [
  { value: 'junior', key: 'studioTools.form.seniorityJunior' },
  { value: 'mid', key: 'studioTools.form.seniorityMid' },
  { value: 'senior', key: 'studioTools.form.senioritySenior' },
  { value: 'lead', key: 'studioTools.form.seniorityLead' },
];

export const APPLICATION_STUDIO_TOOLS: Record<
  ApplicationStudioToolId,
  ApplicationStudioToolConfig
> = {
  cvScore: {
    id: 'cvScore',
    route: '/services/cv-score',
    featureKey: 'individual.cv_fit_report',
    recommendedTier: 'plus',
    generateKind: 'cv_fit_report',
    resultKinds: ['cv_fit_report'],
    titleKey: 'studioTools.cvScore.title',
    subtitleKey: 'studioTools.cvScore.subtitle',
    outcomeKeys: [
      'studioTools.cvScore.outcomeFit',
      'studioTools.cvScore.outcomeProof',
      'studioTools.cvScore.outcomeMoves',
    ],
    generateKey: 'studioTools.cvScore.generate',
    rolePlaceholderKey: 'studioTools.cvScore.rolePlaceholder',
    companyVisible: true,
    countryVisible: true,
    seniorityVisible: true,
    jobDescriptionVisible: true,
    notesVisible: true,
  },
  coverLetter: {
    id: 'coverLetter',
    route: '/services/cover-letters',
    featureKey: 'individual.cover_letter',
    recommendedTier: 'plus',
    generateKind: 'cover_letter',
    resultKinds: ['cover_letter'],
    titleKey: 'studioTools.coverLetter.title',
    subtitleKey: 'studioTools.coverLetter.subtitle',
    outcomeKeys: [
      'studioTools.coverLetter.outcomeOpening',
      'studioTools.coverLetter.outcomeEvidence',
      'studioTools.coverLetter.outcomeClose',
    ],
    generateKey: 'studioTools.coverLetter.generate',
    rolePlaceholderKey: 'studioTools.coverLetter.rolePlaceholder',
    companyVisible: true,
    countryVisible: false,
    seniorityVisible: false,
    jobDescriptionVisible: true,
    notesVisible: true,
  },
  interviewPrep: {
    id: 'interviewPrep',
    route: '/services/interview-prep',
    featureKey: 'individual.interview_prep',
    recommendedTier: 'pro',
    generateKind: 'interview_prep',
    resultKinds: ['interview_prep'],
    titleKey: 'studioTools.interviewPrep.title',
    subtitleKey: 'studioTools.interviewPrep.subtitle',
    outcomeKeys: [
      'studioTools.interviewPrep.outcomeQuestions',
      'studioTools.interviewPrep.outcomeStories',
      'studioTools.interviewPrep.outcomePlan',
    ],
    generateKey: 'studioTools.interviewPrep.generate',
    rolePlaceholderKey: 'studioTools.interviewPrep.rolePlaceholder',
    companyVisible: true,
    countryVisible: false,
    seniorityVisible: true,
    jobDescriptionVisible: true,
    notesVisible: true,
  },
  salaryInsights: {
    id: 'salaryInsights',
    route: '/services/salary-insights',
    featureKey: 'individual.salary_insights',
    recommendedTier: 'pro',
    generateKind: 'salary_report',
    resultKinds: ['salary_report'],
    titleKey: 'studioTools.salaryInsights.title',
    subtitleKey: 'studioTools.salaryInsights.subtitle',
    outcomeKeys: [
      'studioTools.salaryInsights.outcomeRange',
      'studioTools.salaryInsights.outcomeConfidence',
      'studioTools.salaryInsights.outcomeScript',
    ],
    generateKey: 'studioTools.salaryInsights.generate',
    rolePlaceholderKey: 'studioTools.salaryInsights.rolePlaceholder',
    companyVisible: false,
    countryVisible: true,
    seniorityVisible: true,
    jobDescriptionVisible: false,
    notesVisible: true,
  },
  applicationPack: {
    id: 'applicationPack',
    route: '/services/application-pack',
    featureKey: 'individual.application_packet',
    recommendedTier: 'plus',
    generateKind: 'application_packet',
    resultKinds: [
      'cv_fit_report',
      'cover_letter',
      'screening_answers',
      'interview_prep',
      'salary_report',
      'recruiter_preview',
      'apply_checklist',
    ],
    titleKey: 'studioTools.applicationPack.title',
    subtitleKey: 'studioTools.applicationPack.subtitle',
    outcomeKeys: [
      'studioTools.applicationPack.outcomePacket',
      'studioTools.applicationPack.outcomeAnswers',
      'studioTools.applicationPack.outcomeChecklist',
    ],
    generateKey: 'studioTools.applicationPack.generate',
    rolePlaceholderKey: 'studioTools.applicationPack.rolePlaceholder',
    companyVisible: true,
    countryVisible: true,
    seniorityVisible: true,
    jobDescriptionVisible: true,
    notesVisible: true,
  },
  recruiterPreview: {
    id: 'recruiterPreview',
    route: '/services/recruiter-preview',
    featureKey: 'individual.recruiter_preview',
    recommendedTier: 'pro',
    generateKind: 'recruiter_preview',
    resultKinds: ['recruiter_preview'],
    titleKey: 'studioTools.recruiterPreview.title',
    subtitleKey: 'studioTools.recruiterPreview.subtitle',
    outcomeKeys: [
      'studioTools.recruiterPreview.outcomeRead',
      'studioTools.recruiterPreview.outcomeRisks',
      'studioTools.recruiterPreview.outcomeEvidence',
    ],
    generateKey: 'studioTools.recruiterPreview.generate',
    rolePlaceholderKey: 'studioTools.recruiterPreview.rolePlaceholder',
    companyVisible: true,
    countryVisible: true,
    seniorityVisible: true,
    jobDescriptionVisible: true,
    notesVisible: true,
  },
};

export function artifactKindLabel(kind: ApplicationStudioArtifactKind) {
  switch (kind) {
    case 'cv_fit_report':
      return 'CV Fit Report';
    case 'cover_letter':
      return 'Cover Letter';
    case 'interview_prep':
      return 'Interview Prep';
    case 'salary_report':
      return 'Salary Report';
    case 'application_packet':
      return 'Application Packet';
    case 'recruiter_preview':
      return 'Recruiter Preview';
    case 'screening_answers':
      return 'Screening Answers';
    case 'apply_checklist':
      return 'Apply Checklist';
    default:
      return 'Artifact';
  }
}
